import React, { lazy, Suspense } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { Box, Loader } from '@flexisaf/flexibull2';
import {
  canViewApplications,
  canViewStatistics,
  canViewForms,
  canViewUsers,
  canViewPlatform,
  canViewRoles,
  canViewCourses,
  canViewCandidates,
  canViewCandidateGroups,
  canViewTransactions,
  canViewReminder,
  canViewCoupons,
  canViewAdminlevelItem,
  canViewAdminlevel,
  canViewTemplates,
  canViewApplicationGroup,
  canViewApplicationGroupItem,
  canViewSystemSettings,
  canViewPaylog,
  hasAccess,
  canHandleReport,
  canHandleEmail,
  Permissions
} from 'components/CanView';
import { Page403 } from 'components/Page403';

const AdminLevels = lazy(() => import('containers/AdminLevels'));
const Applicants = lazy(() => import('containers/Applicants'));
const ProcessAi = lazy(() => import('containers/Process-Ai'));
const Applications = lazy(() => import('containers/Applications'));
const ApplicationEditor = lazy(() => import('containers/Applications/ApplicationEditor'));
const Course = lazy(() => import('containers/Courses'));
const CourseDetails = lazy(() => import('containers/Courses/CourseDetails'));
const Dashboard = lazy(() => import('containers/Dashboard'));
const Forms = lazy(() => import('containers/Forms'));
const InstitutionConfiguration = lazy(() => import('containers/Institution'));
const ApplicationGroupings = lazy(() => import('containers/ApplicationGroups'));
const UserProfile = lazy(() => import('containers/UserProfile'));
const UploadedCandidates = lazy(() => import('containers/UploadedCandidates'));
const ManageTemplate = lazy(() => import('containers/Template/manageTemplate'));
const DiscountCode = lazy(() => import('containers/Coupon'));
const ManageCoupon = lazy(() => import('containers/Coupon/CouponDetails'));
const Reminders = lazy(() => import('containers/Reminders'))
const AdmissionReports = lazy(() => import('containers/AdmissionReports'));

const WorkflowSteps = lazy(() => import('containers/Workflow/Steps'));
const Workflow = lazy(() => import('containers/Workflow'));
const AuditLog = lazy(() => import('containers/AuditLog'));
const ModificationLog = lazy(() => import('containers/AuditLog/ModificationLog'));
const AddOn = lazy(() => import('containers/AddOns'));
const SmsConfiguration = lazy(() => import('containers/SmsConfiguration'));
const NinLog = lazy(() => import('containers/NinLog'));

const ManageApplication = lazy(() =>
  import('containers/Applications/ManageApplication')
);
const PlatformInstitutions = lazy(() => import('containers/PlatformInstitutions'));
const PlatformOperators = lazy(() => import('containers/PlatformOperators'));
const ManageCandidates = lazy(() => import('containers/UploadedCandidates/ManageCandidates'))
const LocalPaymentConfiguration = lazy(() =>
  import('containers/PaymentConfiguration/LocalPayment')
);
const InternationalPaymentConfiguration = lazy(() =>
  import('containers/PaymentConfiguration/InternationalPayment')
);
const Template = lazy(() => import('containers/Template'));
const Transactions = lazy(() => import('containers/Transactions'));
const AdminPaymentStatus = lazy(() => import('containers/PaymentStatus'));
const Users = lazy(() => import('containers/Users'));
const GoogleAuth = lazy(() => import('components/GoogleAuth'));

const LazyLoader = () => (
  <Box width="100%" height="calc(100vh - 200px)" vAlign>
    <Box width="100%" align="center">
      <Loader />
    </Box>
  </Box>
);

const routeList = [
  {
    path: '/:orgName/dashboard',
    component: Dashboard,
    can: canViewStatistics,
    exact: true
  },
  {
    path: '/:orgName/applications/create',
    component: ApplicationEditor,
    can: [Permissions.CREATE_APPLICATIONS, Permissions.UPDATE_APPLICATIONS],
  },
  {
    path: '/:orgName/applications',
    component: Applications,
    can: canViewApplications,
  },
  {
    path: '/:orgName/manage-applications/:applicationId',
    component: ManageApplication,
    can: canViewApplications,
  },
  {
    path: '/:orgName/courses/:id',
    component: CourseDetails,
    can: canViewCourses,
  },
  {
    path: '/:orgName/courses',
    component: Course,
    can: canViewCourses,
  },
  {
    path: '/:orgName/admin-levels-item',
    component: AdminLevels,
    can: [...canViewAdminlevel, ...canViewAdminlevelItem]
  },
  {
    path: '/:orgName/forms',
    component: Forms,
    can: canViewForms,
  },
  {
    path: '/:orgName/templates',
    component: Template,
    can: canViewTemplates
  },
  {
    path: '/:orgName/workflow/:id',
    component: WorkflowSteps,
    can: canViewTemplates
  },
  {
    path: '/:orgName/workflow',
    component: Workflow,
    can: canViewTemplates
  },
  {
    path: '/:orgName/manage-template',
    component: ManageTemplate,
    can: canViewTemplates
  },
  {
    path: '/:orgName/applicants',
    component: Applicants,
    can: canViewCandidates,
  },
  {
    path: '/:orgName/process-ai',
    component: ProcessAi,
    can: canViewCandidates,
  },
  {
    path: '/:orgName/transactions',
    component: Transactions,
    can: canViewTransactions,
  },
  {
    path: '/:orgName/payment-status',
    component: AdminPaymentStatus,
    can: canViewCandidates,
  },
  {
    path: '/:orgName/notifications',
    component: Reminders,
    can: [...canViewReminder, ...canHandleReport, ...canHandleEmail],
  },
  {
    path: '/:orgName/discount-code',
    component: DiscountCode,
    can: canViewCoupons,
  },
  {
    path: '/:orgName/add-on',
    component: AddOn,
    can: canViewSystemSettings,
  },
  {
    path: '/:orgName/sms-configuration',
    component: SmsConfiguration,
    can: canViewSystemSettings,
  },
  {
    path: '/:orgName/nin-log',
    component: NinLog,
    can: canViewSystemSettings,
  },
  {
    path: '/:orgName/manage-discount-code/:id',
    component: ManageCoupon,
    can: canViewCoupons,
  },
  {
    path: '/:orgName/audit-log',
    component: AuditLog,
    can: canViewTransactions,
  },
  {
    path: '/:orgName/modification-log',
    component: ModificationLog,
    can: canViewTransactions,
  },
  {
    path: '/:orgName/users',
    component: Users,
    can: [...canViewUsers, ...canViewRoles],
  },
  {
    path: '/:orgName/platform/institutions/:institutionId',
    component: PlatformInstitutions,
    can: canViewPlatform,
  },
  {
    path: '/:orgName/platform/institutions',
    component: PlatformInstitutions,
    can: canViewPlatform,
    exact: true,
  },
  {
    path: '/:orgName/platform/operators',
    component: PlatformOperators,
    can: canViewPlatform,
    exact: true,
  },
  {
    path: '/:orgName/application-groupings',
    component: ApplicationGroupings,
    can: [...canViewApplicationGroup, ...canViewApplicationGroupItem],
  },
  {
    path: '/:orgName/configurations',
    component: InstitutionConfiguration,
    can: canViewSystemSettings
  },
  {
    path: '/:orgName/local-payments',
    component: LocalPaymentConfiguration,
    can: canViewPaylog
  },
  {
    path: '/:orgName/international-payments',
    component: InternationalPaymentConfiguration,
    can: canViewPaylog
  },
  {
    path: '/:orgName/uploaded-candidates',
    component: UploadedCandidates,
    can: canViewCandidateGroups,
  },
  {
    path: '/:orgName/manage-candidates/:id',
    component: ManageCandidates,
    can: canViewCandidateGroups,
  },
  {
    path: '/:orgName/admission-reports',
    component: AdmissionReports,
    can: canViewCandidates,
  },
]

const Routes = () => (
  <Suspense fallback={<LazyLoader />}>
    <Switch>
      {
        routeList.map((route, index) => (
          <Route
            {...route}
            component={
              String(route.path || '').includes('/platform/')
                ? route.component
                : hasAccess(route?.can)
                ? route.component
                : Page403
            }
            key={index}
          />
        ))
      }
      <Route path="/:orgName/auth" component={GoogleAuth} />
      <Route path="/:orgName/user-profile" component={UserProfile} />
      <Redirect to='/:orgName/404' />
    </Switch>
  </Suspense>
);

export const menuItems = (orgName, aliases = { course: 'course' }) => [
  {
    name: 'dashboard',
    icon: 'saf-gauge',
    label: 'Dashboard',
    id: 'dashboard',
    navlink: `/${orgName}/dashboard`,
    can: canViewStatistics,
  },
  {
    name: 'applications',
    icon: 'saf-vcard',
    label: 'Applications',
    navlink: `/${orgName}/applications`,
    can: canViewApplications,
  },
  {
    name: 'courses',
    icon: 'saf-flow-tree',
    label: `${aliases?.course}`,
    navlink: `/${orgName}/courses`,
    can: canViewCourses,
  },
  {
    name: 'forms',
    icon: 'saf-wpforms',
    label: 'Forms',
    navlink: `/${orgName}/forms`,
    can: canViewForms,
  },
  {
    name: 'manage-applicants',
    icon: 'saf-user',
    label: 'Process Applicants',
    navlink: `/${orgName}/applicants`,
    can: canViewCandidates,
  },
  {
    name: 'uploaded-candidates',
    icon: 'saf-address-card-o',
    label: 'Uploaded Candidates',
    navlink: `/${orgName}/uploaded-candidates`,
    can: canViewCandidateGroups,
  },
  {
    name: 'admission-reports',
    icon: 'saf-article-alt',
    label: 'Admission Reports',
    navlink: `/${orgName}/admission-reports`,
    can: canViewCandidates,
  },
  {
    name: 'transactions',
    icon: 'saf-history',
    label: 'Transactions',
    navlink: `/${orgName}/transactions`,
    can: canViewTransactions,
  },
  {
    name: 'manage-users',
    icon: 'saf-users-outline',
    label: 'Users',
    navlink: `/${orgName}/users`,
    can: canViewUsers,
  },
  {
    name: 'platform-institutions',
    icon: 'saf-home',
    label: 'Institutions',
    navlink: `/${orgName}/platform/institutions`,
    can: canViewPlatform,
  },
];

export default Routes;
